import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { REVIEWS, BUSINESS } from "../data/products";

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={16}
        className={i <= n ? "text-[#ffd60a] fill-[#ffd60a]" : "text-zinc-700"}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [index, setIndex] = useState(0);
  const perView = 3;
  const maxStart = Math.max(0, REVIEWS.length - perView);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxStart, i + 1));

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm uppercase tracking-[0.25em] text-zinc-500">
              Customer Reviews
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4">
              Loved by the{" "}
              <span className="text-gradient">Maple Shade</span> community
            </h2>
            <div className="flex items-center gap-3 mt-5">
              <Stars n={5} />
              <span className="text-zinc-300 text-sm">
                4.9 average from local customers
              </span>
            </div>
          </motion.div>

          <div className="flex items-center gap-3">
            <button
              data-testid="reviews-prev-button"
              onClick={prev}
              disabled={index === 0}
              className="w-11 h-11 rounded-full glass flex items-center justify-center text-white disabled:opacity-30 hover:bg-white/10 transition"
              aria-label="Previous reviews"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="reviews-next-button"
              onClick={next}
              disabled={index === maxStart}
              className="w-11 h-11 rounded-full glass flex items-center justify-center text-white disabled:opacity-30 hover:bg-white/10 transition"
              aria-label="Next reviews"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {REVIEWS.slice(index, index + perView).map((r, i) => (
                <div
                  key={`${index}-${i}`}
                  data-testid={`review-card-${index + i}`}
                  className="glass rounded-2xl p-7 flex flex-col"
                >
                  <Stars n={r.rating} />
                  <p className="mt-5 text-zinc-300 leading-relaxed text-[15px] flex-1">
                    “{r.text}”
                  </p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#00e5ff] to-[#39ff14] flex items-center justify-center text-black font-semibold">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">
                        {r.name}
                      </div>
                      <div className="text-xs text-zinc-500">Google Review</div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="text-center mt-12">
          <a
            data-testid="reviews-google-button"
            href={BUSINESS.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[#00e5ff]/40 text-[#00e5ff] font-medium hover:bg-[#00e5ff]/10 transition-all duration-300 hover:-translate-y-0.5"
          >
            Read More Reviews on Google <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};
